import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Calendar as CalIcon, Plus } from "lucide-react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { CONSULTATION_TYPES, STATUS_LABELS } from "@/lib/aga";

const typeLabel = Object.fromEntries(CONSULTATION_TYPES.map((t) => [t.value, t.label]));
const typeColor = {
    ambulatorial: "bg-[#DAE3D5] text-[#1E3F33]",
    domiciliar: "bg-[#F4EFE6] text-[#2A5746]",
    telemedicina_canabis: "bg-[#F9ECEC] text-[#9E2A2B]",
};

function startOfWeek(d) {
    const x = new Date(d);
    const day = x.getDay(); // 0-Sun ... 6-Sat
    const diff = (day + 6) % 7; // Monday start
    x.setDate(x.getDate() - diff);
    x.setHours(0, 0, 0, 0);
    return x;
}
const WEEK_LABEL = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

export default function CalendarPage() {
    const [consultations, setConsultations] = useState([]);
    const [anchor, setAnchor] = useState(startOfWeek(new Date()));
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            const { data } = await api.get("/consultations");
            setConsultations(data);
        })();
    }, []);

    const days = useMemo(() => {
        const arr = [];
        for (let i = 0; i < 7; i++) {
            const d = new Date(anchor);
            d.setDate(anchor.getDate() + i);
            arr.push(d);
        }
        return arr;
    }, [anchor]);

    const byDay = useMemo(() => {
        const map = {};
        for (const c of consultations) {
            if (!c.scheduled_at) continue;
            const key = new Date(c.scheduled_at).toDateString();
            if (!map[key]) map[key] = [];
            map[key].push(c);
        }
        return map;
    }, [consultations]);

    const unscheduled = consultations.filter(c => !c.scheduled_at);

    const shift = (delta) => {
        const d = new Date(anchor);
        d.setDate(d.getDate() + delta * 7);
        setAnchor(d);
    };
    const toDay = (d) => d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
    const weekRange = `${toDay(days[0])} — ${toDay(days[6])}`;

    return (
        <AppShell>
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--brand-secondary)] mb-1">Agenda</div>
                    <h1 className="font-display text-3xl font-medium text-brand">Calendário semanal</h1>
                </div>
                <div className="flex items-center gap-2">
                    <button data-testid="cal-prev" onClick={() => shift(-1)} className="btn-secondary text-sm"><ChevronLeft size={14} /></button>
                    <div className="px-3 py-1.5 rounded-lg bg-alt text-brand font-medium text-sm min-w-[180px] text-center">{weekRange}</div>
                    <button data-testid="cal-next" onClick={() => shift(1)} className="btn-secondary text-sm"><ChevronRight size={14} /></button>
                    <button onClick={() => setAnchor(startOfWeek(new Date()))} className="btn-ghost text-sm">Hoje</button>
                    <Link to="/consultations/new" className="btn-primary text-sm"><Plus size={14} /> Nova consulta</Link>
                </div>
            </div>

            <div className="grid grid-cols-7 gap-2 mb-6">
                {days.map((d, i) => {
                    const items = byDay[d.toDateString()] || [];
                    const isToday = d.toDateString() === new Date().toDateString();
                    return (
                        <div key={i} className={`card-earthy min-h-[220px] ${isToday ? "border-2 border-[var(--brand-secondary)]" : ""}`} style={{ padding: "0.75rem" }}>
                            <div className="text-xs font-semibold uppercase tracking-wider text-brand-secondary">{WEEK_LABEL[i]}</div>
                            <div className={`font-display text-2xl ${isToday ? "text-[var(--brand-accent)]" : "text-brand"}`}>{d.getDate()}</div>
                            <div className="mt-2 space-y-1.5">
                                {items.length === 0 && <div className="text-xs text-muted-clinical">—</div>}
                                {items.map((c) => (
                                    <Link key={c.id} to={`/consultations/${c.id}`} className={`block text-xs p-2 rounded-md ${typeColor[c.consultation_type] || "bg-alt text-brand"} hover:opacity-90`}>
                                        <div className="font-medium truncate">{c.patient_name}</div>
                                        <div className="opacity-75 truncate">{new Date(c.scheduled_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })} · {typeLabel[c.consultation_type].split("—")[0]}</div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            {unscheduled.length > 0 && (
                <div className="card-earthy">
                    <div className="flex items-center gap-2 mb-3">
                        <CalIcon size={16} className="text-brand" />
                        <h3 className="font-display text-lg font-medium text-brand">Consultas sem data agendada ({unscheduled.length})</h3>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {unscheduled.map((c) => (
                            <Link key={c.id} to={`/consultations/${c.id}`} data-testid={`unscheduled-${c.id}`} className="block p-3 rounded-lg bg-alt hover:opacity-90">
                                <div className="text-sm font-medium text-brand">{c.patient_name}</div>
                                <div className="text-xs text-muted-clinical mt-1">{typeLabel[c.consultation_type]} · {STATUS_LABELS[c.status]}</div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </AppShell>
    );
}
